import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import { FetchOptionsFunction } from '@asset-metrix/portal-ui';
import { JsonSchema } from '@sagold/react-json-editor';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { fetchDocUploadModalValues } from '../../api/portalGateway/docviewUploadFormValues';

import { MultiStepUpload } from './MultiStepUpload';
import { SingleStepUpload } from './SingleStepUpload';
import { DocUploadConfig, useDocUploadStore } from './store';

export type DocUploadModalProps = {
  acceptedMimeTypes?: string[];
  contentId: string;
  tableFilterInstanceId: string;
  enableAIMetadataExtraction: boolean;
  enablePatternBasedRenaming: boolean;
  fileNameMetadataExtractionConfig: FileUploadMetaDataExtractionDto | undefined;
  jsonSchema: JsonSchema;
  onClose: () => void;
  onUploadSuccess?: () => Promise<unknown>;
  uploadFilenamePattern?: string;
  withinPortal?: boolean;
};

export function DocUploadModal({
  acceptedMimeTypes,
  contentId,
  tableFilterInstanceId,
  enableAIMetadataExtraction,
  enablePatternBasedRenaming,
  fileNameMetadataExtractionConfig,
  jsonSchema,
  onClose,
  onUploadSuccess,
  uploadFilenamePattern,
  withinPortal,
}: DocUploadModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isTableModalValuesLoading = useIsFetching({
    queryKey: ['tableModalValues'],
  });

  const {
    fileNameMetadataExtractionEnabled,
    hasPerFileInputInUpload,
    initialize,
    setTableModalValues,
    setModalError,
    setIsTableModalValuesLoading,
    storeInitialized,
  } = useDocUploadStore(
    useShallow((state) => ({
      fileNameMetadataExtractionEnabled:
        state.fileNameMetadataExtractionEnabled,
      hasPerFileInputInUpload: state.hasPerFileInputInUpload,
      initialize: state.initialize,
      setTableModalValues: state.setTableModalValues,
      setModalError: state.setModalError,
      setIsTableModalValuesLoading: state.setIsTableModalValuesLoading,
      storeInitialized: state.storeInitialized,
    }))
  );

  const fetchOptions = useCallback<FetchOptionsFunction>(
    async (filter) => {
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ['tableModalValues', { contentId, filter }],
          queryFn: fetchDocUploadModalValues,
        });
        setModalError(undefined);
        setTableModalValues(result);
        return result || {};
      } catch (error) {
        setModalError(t('app.upload.conditionalFilterOptionsError'));
        throw error;
      }
    },
    [contentId, queryClient, setTableModalValues, setModalError, t]
  );

  const config = useMemo<DocUploadConfig>(
    () => ({
      acceptedMimeTypes,
      contentId,
      tableFilterInstanceId,
      enableAIMetadataExtraction,
      enablePatternBasedRenaming,
      fileNameMetadataExtractionConfig,
      fetchOptions,
      jsonSchema,
      onClose,
      onUploadSuccess,
      translation: t,
      uploadFilenamePattern,
      withinPortal,
    }),
    [
      acceptedMimeTypes,
      contentId,
      tableFilterInstanceId,
      enableAIMetadataExtraction,
      enablePatternBasedRenaming,
      fileNameMetadataExtractionConfig,
      fetchOptions,
      jsonSchema,
      onClose,
      onUploadSuccess,
      t,
      uploadFilenamePattern,
      withinPortal,
    ]
  );

  useEffect(() => {
    initialize(config);
  }, [config, initialize]);

  useEffect(() => {
    setIsTableModalValuesLoading(Boolean(isTableModalValuesLoading));
  }, [isTableModalValuesLoading, setIsTableModalValuesLoading]);

  const isMultiStepUploadEnabled =
    hasPerFileInputInUpload || fileNameMetadataExtractionEnabled;

  if (!storeInitialized) {
    return null;
  }

  if (isMultiStepUploadEnabled) {
    return <MultiStepUpload />;
  }

  return <SingleStepUpload />;
}
